import { useEffect, useState } from 'react';
import { api } from '../api';
import { sar } from '../format';

interface Bank { bank: string; balance: number; account_last4: string; threshold: number }
interface PnL { revenue: number; cogs: number; expenses: number; net: number }
interface Alert { code: string; level: 'warn' | 'critical'; message: string }
interface Payload {
  lens: 'L02'; date: string;
  cash_by_bank: Bank[];
  mtd_pnl: PnL;
  forecast_30d: { date: string; projected: number }[];
  alerts: Alert[];
  _meta: { phase: number; mode: string; source: string };
}

export function LensFinancial() {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { api<Payload>('/lenses/L02').then(setData).catch((e) => setErr(String(e))); }, []);
  if (err) return <div className="lens error">خطأ: {err}</div>;
  if (!data) return <div className="lens">جارٍ التحميل…</div>;

  const totalCash = data.cash_by_bank.reduce((s, b) => s + b.balance, 0);

  return (
    <div className="lens">
      <header>
        <h2>L02 — المالية والتوقع</h2>
        <span className="badge">{data.date}</span>
        <span className="badge src">{data._meta.source}</span>
      </header>

      <section className="widgets">
        <div className="widget"><h3>إجمالي السيولة</h3><p className="big">{sar(totalCash)}</p><p className="hint">{data.cash_by_bank.length} حساب</p></div>
        <div className="widget"><h3>صافي MTD</h3><p className="big">{sar(data.mtd_pnl.net)}</p><p className="hint">إيراد {sar(data.mtd_pnl.revenue)}</p></div>
        <div className="widget"><h3>تنبيهات</h3><p className="big">{data.alerts.length}</p><p className="hint">cash + forecast</p></div>
      </section>

      {data.alerts.length > 0 && (
        <section className="panel">
          <h3>تنبيهات</h3>
          <ul className="cards">
            {data.alerts.map((a) => (
              <li key={a.code} className={`card u-${a.level}`}>
                <div className="card-head">
                  <span className={`pill u-${a.level}`}>{a.level}</span>
                  <span className="lens-tag">{a.code}</span>
                </div>
                <p className="card-title">{a.message}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h3>السيولة حسب البنك</h3>
        <table className="banks">
          <thead><tr><th>البنك</th><th>الحساب</th><th>الرصيد</th><th>Threshold</th><th>الحالة</th></tr></thead>
          <tbody>
            {data.cash_by_bank.map((b) => {
              const low = b.balance < b.threshold * 1.1;
              return (
                <tr key={b.account_last4}>
                  <td>{b.bank}</td>
                  <td>*{b.account_last4}</td>
                  <td className="num">{sar(b.balance)}</td>
                  <td className="num">{sar(b.threshold)}</td>
                  <td>{low ? <span className="pill u-warn">منخفض</span> : <span className="pill u-info">جيّد</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <h3>P&L الشهر حتى اليوم</h3>
        <div className="kv">
          <div><span>الإيراد</span><strong>{sar(data.mtd_pnl.revenue)}</strong></div>
          <div><span>تكلفة المبيعات</span><strong>{sar(data.mtd_pnl.cogs)}</strong></div>
          <div><span>مصاريف تشغيلية</span><strong>{sar(data.mtd_pnl.expenses)}</strong></div>
          <div className="kv-total"><span>الصافي</span><strong>{sar(data.mtd_pnl.net)}</strong></div>
        </div>
      </section>

      <footer className="meta">
        <span>توقّع 30 يوم: {data.forecast_30d.length} نقطة</span>
        <span>phase: {data._meta.phase}</span>
        <span>mode: {data._meta.mode}</span>
      </footer>
    </div>
  );
}
